import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Popover } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { Skeleton } from '@material-ui/lab';
import useKeyBoardEventHandlers from '~/modules/common/hooks/useKeyBoardEventHandlers';
import { Money2, MoneyValue, NoValue } from '~/modules/common/components';

const rateLabel = <FormattedMessage id="customLineItem.rate" />;

const useStyles = makeStyles(theme => ({
  skeletonWrapper: {
    width: '100%'
  },
  hover: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer'
    }
  }
}));

const usePopoverStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(1.5),
    width: theme.spacing(32)
  }
}));

const popoverOrigin = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'right'
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'right'
  }
};

const RateRenderer = ({
  field,
  record,
  column: {
    isLineItemUpdating,
    editable,
    onCustomLineItemRateChange,
    onCustomLineItemBlur
  }
}) => {
  const classes = useStyles();
  const popoverClasses = usePopoverStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = useCallback(
    event => {
      if (editable) setAnchorEl(event.currentTarget);
    },
    [editable]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const { handleKeyDown } = useKeyBoardEventHandlers({
    onEnterClick: handleOpen
  });

  const money = useMemo(() => record[field] || null, [record, field]);

  const rateChangeHandler = useCallback(
    e => onCustomLineItemRateChange(e, record),
    [onCustomLineItemRateChange, record]
  );

  const customLineItemBlurHandler = useCallback(
    value => {
      onCustomLineItemBlur(value, record);
      setAnchorEl(null);
    },
    [onCustomLineItemBlur, record]
  );

  if (isLineItemUpdating)
    return (
      <div className={classes.skeletonWrapper}>
        <Skeleton variant="text" width="100%" height={24} />
      </div>
    );

  return (
    <>
      <div
        role="button"
        className={editable ? classes.hover : undefined}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        tabIndex={editable ? 0 : -1}
      >
        {money ? <MoneyValue money={money} /> : <NoValue />}
      </div>
      {anchorEl && (
        <Popover
          id="rate-editor"
          anchorEl={anchorEl}
          open
          onClose={handleClose}
          classes={popoverClasses}
          {...popoverOrigin}
        >
          <Money2
            id="lineItems.custom.rate"
            variant="filled"
            label={rateLabel}
            currencyEditable={false}
            amount={money?.amount || 0}
            currency={money?.currency || record.amount.currency}
            allowNegative
            onAmountChange={rateChangeHandler}
            onAmountBlur={customLineItemBlurHandler}
            data-qe-id="lineItems.custom.rate"
            autoFocus
          />
        </Popover>
      )}
    </>
  );
};

RateRenderer.propTypes = {
  column: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
  record: PropTypes.object.isRequired
};

export default RateRenderer;
